/**
 * Sérialise les blocs du canvas en markdown — le sens de lecture qui manquait.
 *
 * Toute la plomberie du dépôt allait dans un seul sens : markdown → blocs
 * (`blocksToHtml.ts`, `markdownToDocxParagraphs.ts`, le `StructuralParser`).
 * Le retour n'existait nulle part côté web ; côté Word il existe
 * (`reformulationHelpers.ts`) mais n'est jamais envoyé au backend — l'add-in
 * POSTe du texte plat lu par `Office.FileType.Text`, sans aucun `#`.
 *
 * Ce module sert la capture du plan au clic « Lancer » de la génération
 * (`redaction_plan_response.plan_markdown`). La barre à franchir est donc
 * basse : le backend n'impose aucun format sur ce champ — il l'injecte
 * verbatim dans un prompt et ne valide que « non vide ». Reproduire Word,
 * c'est produire du texte ordonné ; restituer les niveaux de titre fait
 * strictement mieux, pour quelques lignes de plus.
 */

/**
 * Ce qu'un bloc doit porter pour être sérialisable. Volontairement structurel
 * et minimal plutôt qu'un import du type de snapshot : ce module ne doit pas
 * dépendre de la surface d'inspection de l'agent, qui bouge pour d'autres
 * raisons. Les champs sont ceux que `doc.blocks.list({ includeText: true })`
 * publie réellement en superdoc@2.6.0.
 */
export interface BlocSerialisable {
  text?: string | null;
  nodeType?: string | null;
  /** Publié par `blocks.list` sur les blocs à style nommé (mesuré). */
  headingLevel?: number | null;
  /** Repli quand `headingLevel` est absent : `Hector-H1`…`Hector-H6`. */
  styleId?: string | null;
}

/** `Hector-H3` → 3. Rend `null` pour tout ce qui n'est pas un titre Hector. */
function niveauDepuisLeStyle(styleId: string | null | undefined): number | null {
  if (!styleId) return null;
  const trouve = /^Hector-H([1-6])$/.exec(styleId.trim());
  return trouve ? Number(trouve[1]) : null;
}

/**
 * Niveau de titre d'un bloc, ou `null` si c'en n'est pas un.
 *
 * WHY deux sources : `headingLevel` n'est publié que sur les blocs à style
 * nommé (relevé `tests/ws-mocked/mesure-lecture-par-bloc.spec.ts`), et un plan
 * peut porter des titres dont seul le `styleId` est renseigné. Prendre les deux
 * coûte trois lignes ; n'en prendre qu'une aplatit une partie du plan en
 * silence — et un plan aplati redevient exactement ce que Word envoie.
 */
export function niveauDeTitre(bloc: BlocSerialisable): number | null {
  if (typeof bloc.headingLevel === 'number' && bloc.headingLevel >= 1 && bloc.headingLevel <= 6) {
    return bloc.headingLevel;
  }
  return niveauDepuisLeStyle(bloc.styleId);
}

/**
 * Sérialise une liste de blocs en markdown.
 *
 * ⚠️ **Les blocs vides sont écartés, et ce n'est pas de la cosmétique.** Une
 * suppression suivie d'un bloc ENTIER laisse le bloc dans `blocks.list` avec
 * `text: ''` — mesuré le 2026-08-28 sur superdoc@2.6.0
 * (`tests/ws-mocked/mesure-suppression-suivie-dans-le-texte.spec.ts` : le
 * `nodeId` reste listé, le total de blocs ne bouge pas, seul le texte se vide).
 * Sans ce filtre, un plan capturé après quelques suppressions se remplit de
 * paragraphes fantômes que le générateur prendrait pour des sections vides.
 *
 * La même mesure établit la bonne nouvelle : `blocks.list` publie l'état
 * « toutes révisions acceptées ». Le texte supprimé en suivi n'en sort pas, et
 * le texte inséré en suivi en sort. La capture reflète donc ce que l'avocat
 * obtiendrait en acceptant tout — jamais les moyens qu'il vient d'abandonner.
 */
export function blocsVersMarkdown(blocs: readonly BlocSerialisable[]): string {
  const lignes: string[] = [];
  for (const bloc of blocs) {
    const texte = (bloc.text ?? '').trim();
    if (!texte) continue;
    const niveau = niveauDeTitre(bloc);
    lignes.push(niveau === null ? texte : `${'#'.repeat(niveau)} ${texte}`);
  }
  return lignes.join('\n\n');
}
